/**
 * Node Editor Registry
 * 
 * Defines how each funnel node type is edited.
 * Each node type can have:
 * - An inline editor (React component rendered in the side panel)
 * - An iframe editor (external app embedded via Builder Protocol)
 * 
 * This is the extensibility point — future node types (email, events, webhooks)
 * register here and the funnel editor renders the appropriate editor.
 */

import type { StepType } from '../../app/stores/[id]/storefront/types'

export type NodeEditorMode = 'iframe' | 'inline'

export interface NodeEditorConfig {
  /** How the editor renders */
  mode: NodeEditorMode
  /** For iframe mode: URL of the external editor app */
  iframeUrl?: string
  /** For iframe mode: protocol identifier */
  protocol?: string
  /** For inline mode: component key (mapped to actual React components in the renderer) */
  inlineComponent?: string
  /** Whether the editor supports fullscreen mode */
  supportsFullscreen?: boolean
  /** Label shown in the editor panel header */
  label?: string
}

/**
 * Default registry mapping step types to their editors.
 * 
 * All page types (landing, checkout, offer, thankyou, custom) use the Studio iframe editor.
 * Shopify storefront steps use an inline editor (already built).
 * Future node types (event, email, webhook) will be added here.
 */
export function getNodeEditorConfig(
  stepType: StepType,
  options?: { 
    studioUrl?: string
    /** Whether the step has Studio source data (vs legacy plugin) */
    hasStudioSource?: boolean
    /** External editor URL from plugin manifest */
    externalEditorUrl?: string
  }
): NodeEditorConfig {
  const studioUrl = options?.studioUrl || getDefaultStudioUrl()

  // Shopify storefront steps have their own inline editor
  if (stepType === 'storefront_shopify' || stepType === 'storefront_woocommerce') {
    return {
      mode: 'inline',
      inlineComponent: 'shopify-editor',
      label: 'Storefront Editor',
    }
  }

  // If plugin has a custom external editor URL (third-party builder)
  if (options?.externalEditorUrl) {
    return {
      mode: 'iframe',
      iframeUrl: options.externalEditorUrl,
      protocol: 'builder',
      supportsFullscreen: true,
      label: 'External Editor',
    }
  }

  // If step has Studio source data, use Studio editor
  if (options?.hasStudioSource) {
    return {
      mode: 'iframe',
      iframeUrl: studioUrl,
      protocol: 'builder',
      supportsFullscreen: true,
      label: 'Visual Editor',
    }
  }

  // Default: page types use the legacy step editor (inline)
  // This will be the fallback for plugins that don't have Studio source data
  return {
    mode: 'inline',
    inlineComponent: 'step-editor',
    label: 'Step Settings',
  }
}

/**
 * Get the Studio app URL.
 * In development, this is localhost:3003 (or whatever Vite runs on).
 * In production, this is the deployed Studio URL.
 *
 * Exported because it is the only answer to "which Studio": anything else that
 * needs the URL must call this instead of re-reading VITE_BUILDER_URL with its
 * own fallback, which loses the hostname derivation below on any deploy where
 * the env var is unset (#TAG-2428).
 */
export function getDefaultStudioUrl(): string {
  if (typeof import.meta !== 'undefined') {
    const env = (import.meta as any).env
    const envUrl = env?.VITE_BUILDER_URL || env?.VITE_STUDIO_URL
    if (envUrl) return envUrl
  }

  if (window.location.hostname === 'localhost' || window.location.hostname.endsWith('.localhost')) {
    return 'http://localhost:3003'
  }

  // Derive from current hostname.
  // Examples:
  //   crm.tagada.io           → studio.tagada.io
  //   crm-preprod.tagada.io   → studio-preprod.tagada.io
  //   crmv2.apptics.ai        → studio.apptics.ai   (whitelabel, versioned subdomain)
  //   crm.apptics.ai          → studio.apptics.ai   (whitelabel)
  // Strategy: strip the leading `crm`/`crmvN`/`crm-suffix` label, then prefix `studio.`
  // (or `studio-suffix.` when a dash-suffix was present, to match Tagada's preprod pattern).
  const host = window.location.hostname
  const dashMatch = host.match(/^crm-([^.]+)\.(.+)$/)
  if (dashMatch) {
    return `https://studio-${dashMatch[1]}.${dashMatch[2]}`
  }
  const labelMatch = host.match(/^crm(?:v\d+)?\.(.+)$/)
  if (labelMatch) {
    return `https://studio.${labelMatch[1]}`
  }
  return `https://studio.${host}`
}

/**
 * Future node types (not yet implemented, but shows the extensibility)
 */
export const FUTURE_NODE_TYPES = {
  event: {
    mode: 'inline' as const,
    inlineComponent: 'event-config',
    label: 'Event Configuration',
  },
  email: {
    mode: 'iframe' as const,
    iframeUrl: '', // future email builder URL
    protocol: 'email',
    supportsFullscreen: true,
    label: 'Email Flow',
  },
  webhook: {
    mode: 'inline' as const,
    inlineComponent: 'webhook-config',
    label: 'Webhook',
  },
  delay: {
    mode: 'inline' as const,
    inlineComponent: 'delay-config',
    label: 'Delay',
  },
} as const
